import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl, reelThumbnail } from "../lib/api";
import { StreamHLSPlayer } from "../components/CMSPanel";
import { Play, ArrowUp, ArrowDown, ShareNetwork, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Public "Live Feed" page — TikTok-style vertical snap-scroll of reels.
 *
 * How it works:
 *   • A single scroll-snapping column, one reel per viewport.
 *   • Every card renders the same responsive Cloudflare Stream player, so
 *     portrait & landscape videos both size correctly.
 *   • An IntersectionObserver watches each card; the one that's ≥60% visible
 *     is the "active" one — Stream players other than the active one are
 *     paused (imperatively via a ref) to prevent 8 videos from playing at once
 *     and blowing the browser tab's memory / bandwidth.
 *   • Keyboard: ArrowUp / ArrowDown / j / k navigate between cards.
 */
export default function LiveFeed() {
  const [reels, setReels] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef(null);
  const cardRefs = useRef({});

  useEffect(() => {
    api.get("/reels").then(({ data }) => {
      // Show newest first (fall back to id order if no created_at)
      const sorted = [...data].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setReels(sorted);
    }).catch(() => {});
    document.title = "Live Feed · Wisewit AI";
  }, []);

  // Track which card is centred so we only auto-play one at a time.
  useEffect(() => {
    if (!reels.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const idx = Number(entry.target.getAttribute("data-idx"));
            setActiveIdx(idx);
          }
        }
      },
      { threshold: [0.6], root: containerRef.current },
    );
    Object.values(cardRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [reels]);

  // Keyboard nav — ArrowUp/Down + Vim j/k, so it feels like Reels/TikTok on desktop.
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      let next = null;
      if (["ArrowDown", "j"].includes(e.key)) next = Math.min(reels.length - 1, activeIdx + 1);
      if (["ArrowUp", "k"].includes(e.key)) next = Math.max(0, activeIdx - 1);
      if (next !== null && next !== activeIdx) {
        e.preventDefault();
        cardRefs.current[next]?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeIdx, reels.length]);

  const scrollTo = (idx) => cardRefs.current[idx]?.scrollIntoView({ behavior: "smooth", block: "start" });

  if (!reels.length) {
    return (
      <div className="px-6 py-24" data-testid="live-feed-empty">
        <div className="text-overline mb-4">◢ Live feed</div>
        <h1 className="font-display text-4xl text-white">No reels yet.</h1>
        <p className="mt-3 text-[#8E8E93]">Once you publish reels via the admin CMS, they'll appear here as a scrollable feed.</p>
        <Link to="/admin" className="btn-primary mt-6 inline-flex">Open admin</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative" data-testid="live-feed-page">
      <div className="fixed top-0 left-0 md:left-20 right-0 z-30 px-6 md:px-14 py-4 backdrop-blur bg-[#050505]/60 border-b border-[#2A2A2A] flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">◢ Live feed</div>
          <div className="mt-0.5 font-display text-lg text-white">
            {activeIdx + 1} <span className="text-[#555]">of {reels.length}</span>
          </div>
        </div>
        <Link to="/reels" className="btn-ghost text-[10px] py-2 px-3" data-testid="live-feed-grid-link">Grid view →</Link>
      </div>

      <div
        ref={containerRef}
        data-testid="live-feed-scroller"
        className="h-screen overflow-y-scroll snap-y snap-mandatory scroll-smooth"
        style={{ scrollSnapType: "y mandatory" }}
      >
        {reels.map((r, idx) => (
          <FeedCard
            key={r.id}
            reel={r}
            idx={idx}
            isActive={idx === activeIdx}
            registerRef={(el) => { cardRefs.current[idx] = el; }}
          />
        ))}
      </div>

      {/* Floating up/down nudge for desktop users who don't realise it scrolls */}
      <div className="hidden md:flex fixed right-6 top-1/2 -translate-y-1/2 z-40 flex-col gap-2">
        <button
          onClick={() => scrollTo(Math.max(0, activeIdx - 1))}
          disabled={activeIdx === 0}
          className="w-10 h-10 border border-[#2A2A2A] bg-[#050505]/70 hover:bg-[#0a0a0a] text-white flex items-center justify-center disabled:opacity-30"
          data-testid="live-feed-up"
          aria-label="Previous reel"
        >
          <ArrowUp size={16} />
        </button>
        <button
          onClick={() => scrollTo(Math.min(reels.length - 1, activeIdx + 1))}
          disabled={activeIdx === reels.length - 1}
          className="w-10 h-10 border border-[#2A2A2A] bg-[#050505]/70 hover:bg-[#0a0a0a] text-white flex items-center justify-center disabled:opacity-30"
          data-testid="live-feed-down"
          aria-label="Next reel"
        >
          <ArrowDown size={16} />
        </button>
      </div>
    </div>
  );
}

function FeedCard({ reel, idx, isActive, registerRef }) {
  const isStream = useMemo(
    () => reel.video_url && (/\.m3u8(\?|#|$)/i.test(reel.video_url) || /cloudflarestream\.com/i.test(reel.video_url)),
    [reel.video_url],
  );

  const share = async () => {
    const url = `${window.location.origin}/reels/${reel.slug || reel.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: reel.title, text: reel.what_it_solves, url }); return; } catch { /* cancelled */ }
    }
    try { await navigator.clipboard.writeText(url); toast.success("Reel URL copied to clipboard."); }
    catch { toast.error("Couldn't copy — long-press the address bar."); }
  };

  return (
    <section
      ref={registerRef}
      data-idx={idx}
      data-testid={`feed-card-${idx}`}
      className="h-screen w-full snap-start flex items-center justify-center px-4 md:px-8 py-16 relative"
    >
      <div className="relative w-full max-w-md md:max-w-lg h-[85vh] bg-[#0a0a0a] border border-[#2A2A2A] flex flex-col overflow-hidden">
        <div className="flex-1 bg-[#050505] flex items-center justify-center overflow-hidden">
          {reel.video_url ? (
            isStream ? (
              // Stream player handles portrait/landscape; only autoplay the active card.
              isActive ? (
                <div className="w-full max-h-full"><StreamHLSPlayer src={reel.video_url} autoplay={true} poster={reelThumbnail(reel)} /></div>
              ) : (
                <div className="w-full h-full relative flex items-center justify-center">
                  <img src={reelThumbnail(reel)} alt={reel.title} className="max-w-full max-h-full object-contain opacity-70" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full ww-glass flex items-center justify-center border border-[#FF4500]">
                      <Play size={20} color="#FF4500" weight="fill" />
                    </div>
                  </div>
                </div>
              )
            ) : (
              // Legacy plain-MP4 path — HTML5 video controlled by isActive so
              // scrolled-out cards don't consume bandwidth.
              <video
                key={`${reel.id}-${isActive ? "on" : "off"}`}
                src={mediaUrl(reel.video_url)}
                poster={reelThumbnail(reel)}
                controls
                playsInline
                autoPlay={isActive}
                muted={!isActive}
                className="max-h-full max-w-full w-auto h-auto object-contain"
              />
            )
          ) : (
            <img src={reelThumbnail(reel)} alt={reel.title} className="max-h-full max-w-full object-contain" />
          )}
        </div>
        <div className="p-4 md:p-5 border-t border-[#2A2A2A] bg-[#0a0a0a]">
          <div className="flex items-center justify-between gap-2">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">{reel.category}</div>
            <div className="font-mono text-[10px] text-[#8E8E93]">{reel.duration_sec}s</div>
          </div>
          <h2 className="mt-2 font-display text-xl md:text-2xl text-white leading-tight">{reel.title}</h2>
          <p className="mt-1.5 text-[#8E8E93] text-sm line-clamp-2">{reel.what_it_solves}</p>
          <div className="mt-3 flex items-center gap-2">
            <button onClick={share} className="btn-ghost text-[10px] py-2 px-3" data-testid={`feed-share-${idx}`}>
              <ShareNetwork size={12} /> Share
            </button>
            <Link to={`/reels/${reel.slug || reel.id}`} className="btn-ghost text-[10px] py-2 px-3" data-testid={`feed-open-${idx}`}>
              <ArrowSquareOut size={12} /> Open
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
